import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Tag, Tile } from "@carbon/react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../carbon/DataTable/DataTable";
import { RecentAward } from "../../../types";
import { formatDate } from "../../../utils/utils";
import { formatCurrency } from "../utils/kpiHelpers";
import "./recentAwardsTable.scss";

interface RecentAwardsTableProps {
  awards: RecentAward[];
}

type StatusTagType =
  | "blue"
  | "green"
  | "purple"
  | "cyan"
  | "teal"
  | "red"
  | "gray";

// Line-level states (PurchaseRequestItemState). Anything else falls back to a neutral gray tag.
const STATUS_TAG_TYPE: Record<string, StatusTagType> = {
  AWARDED: "blue",
  PENDING_DELIVERY: "purple",
  DELIVERED: "green",
  QUOTING: "cyan",
  IN_APPROVAL: "teal",
  REJECTED: "red",
  DRAFT: "gray",
};

interface AwardRow extends DataTableRow {
  id: string;
  date: string;
  prCode: string;
  material: string;
  family: string;
  supplierName: string;
  buyerName: string;
  awarded: string;
  saved: string;
  status: ReactNode;
}

const RecentAwardsTable = ({ awards }: RecentAwardsTableProps) => {
  const { t } = useTranslation();

  const renderStatus = (status: string | null): ReactNode => {
    // An award with no purchase-request line has no status: show a muted dash, never a tag.
    if (!status) {
      return (
        <span
          className="recent-awards__no-status"
          data-testid="award-status-none"
          aria-label={t("analytics.recentAwards.noStatus")}
        >
          —
        </span>
      );
    }
    const known = status in STATUS_TAG_TYPE;
    return (
      <Tag
        type={known ? STATUS_TAG_TYPE[status] : "gray"}
        size="md"
        data-testid="award-status"
      >
        {known ? t(`analytics.recentAwards.statuses.${status}`) : status}
      </Tag>
    );
  };

  // RecentAward has no id of its own; a request can award several lines, so key on position too.
  const rows: AwardRow[] = awards.map((a, i) => ({
    id: `${i}-${a.prCode}`,
    date: formatDate(a.date),
    prCode: a.prCode,
    material: a.material,
    family: a.family,
    supplierName: a.supplierName,
    buyerName: a.buyerName,
    awarded: formatCurrency(a.awarded, a.currency),
    saved: formatCurrency(a.saved, a.currency),
    status: renderStatus(a.status),
  }));

  const headers: DataTableColumn<AwardRow>[] = [
    { key: "date", header: t("analytics.recentAwards.date") },
    { key: "prCode", header: t("analytics.recentAwards.request") },
    { key: "material", header: t("analytics.recentAwards.material") },
    { key: "family", header: t("analytics.recentAwards.family") },
    { key: "supplierName", header: t("analytics.recentAwards.supplier") },
    { key: "buyerName", header: t("analytics.recentAwards.buyer") },
    { key: "awarded", header: t("analytics.recentAwards.awarded") },
    { key: "saved", header: t("analytics.recentAwards.saved") },
    { key: "status", header: t("analytics.recentAwards.status") },
  ];

  return (
    <Tile className="recent-awards">
      <h3 className="recent-awards__title">
        {t("analytics.recentAwards.title")}
      </h3>
      <div className="recent-awards__table">
        <DataTable
          headers={headers}
          rows={rows}
          isLoading={false}
          empty={
            <p className="recent-awards__empty">
              {t("analytics.recentAwards.noData")}
            </p>
          }
        />
      </div>
    </Tile>
  );
};

export default RecentAwardsTable;
